/* eslint-disable i18next/no-literal-string */
import { observable, action, computed, reaction, toJS } from 'mobx';
import moment from 'moment';
import _ from 'lodash';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';

import { formatName } from 'shared/helpers/users';

// store data about the current user, guest or authed, in this store
// also see the controller inside of hooks/use-user-controller

// there are distinctly 3 logged in states, not 2
// - Not logged in, guest experience
// - Logged in, has profile and stuff
// - *Logging in*, has no user data but isn't a guest
export default class UserStore {
  @observable id = null;
  @observable profile = {};
  @observable email = null;
  @observable emails = [];
  @observable token = undefined;
  @observable createdAt = null;
  @observable viewOrderId = null;
  @observable refetchRequest = false;
  @observable ageOverride = null;
  @observable loading = null;
  @observable userDetailsFromExternalSource = {};

  constructor() {
    reaction(
      () => (this.emails.length > 0 ? this.emails[0].address : null),
      (email) => {
        if (email) {
          this.email = email;
        }
      }
    );

    this.ageOverride = getPersistedValue('ageOverride', null);
    this.token = getPersistedValue('access-token', null);
  }

  @action
  setAgeOverride(age, rememberMe) {
    const expireDays = rememberMe ? 30 : 1;
    this.ageOverride = {
      expires: moment().add(expireDays, 'days').toISOString(),
      age,
    };

    setPersistedValue('ageOverride', this.ageOverride, !rememberMe);
  }

  @computed
  get isLoggedIn() {
    return !!this.id;
  }

  @computed
  get exists() {
    return this.isLoggedIn;
  }

  @action
  login(loginData) {
    this.setUser(loginData.user);
    this.updateToken(loginData.accessToken);
  }

  @action
  updateToken(token) {
    this.token = token;
    setPersistedValue(`access-token`, token);
  }

  // Because of the way the user store was built and integrated, we have to
  // manually update the user object manually after mutations
  @action
  refetch() {
    this.refetchRequest = true;
  }

  @action
  logout() {
    this.token = null;
    this.id = null;
    this.createdAt = null;
    this.profile = {};
    this.emails = [];
  }

  @action
  setUser(userData) {
    this.id = userData._id;
    this.profile = userData.profile;
    this.emails = userData.emails;
    this.createdAt = userData.createdAt;
  }

  @action
  setUserDetailsFromExternalSource(userDetails) {
    // TODO: we should probably validate the user details in some way?
    this.userDetailsFromExternalSource = userDetails;
  }

  @action
  removeUserDetailsFromExternalSource() {
    this.userDetailsFromExternalSource = {};
  }

  @action
  setMedicalCard = (medicalCard) =>
    _.set(this.profile, `medicalCard`, {
      ...(this.profile.medicalCard || {}),
      ..._.omitBy(medicalCard, _.isEmpty),
    });

  @computed get hasExternalUser() {
    return !_.isEmpty(this.userDetailsFromExternalSource);
  }

  @computed get formatedBirthdate() {
    if (!this.isLoggedIn) {
      return '';
    }
    return moment(this.profile.birthday, 'M/D/YYYY', 'MM/DD/YYYY').format('MM/DD/YYYY');
  }

  @computed get phone() {
    return this.profile.phone;
  }

  @computed get firstName() {
    if (!this.exists) {
      return '';
    }
    return _.get(this.profile, 'firstName', _.split(_.get(this.profile, 'fullName', ''), ' ')[0]);
  }

  @computed get lastName() {
    if (!this.exists) {
      return '';
    }
    return _.get(this.profile, 'lastName', _.split(_.get(this.profile, 'fullName', ''), ' ')[1]);
  }

  @computed get fullName() {
    if (!this.exists) {
      return '';
    }
    return formatName(this.profile);
  }

  @computed get user() {
    return toJS(this);
  }
}
